import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";
import { useMediaQuery } from "react-responsive";
import ProductCarouselSkeleton from "../../Components/ProductCarouselSkeleton";
import { addProductImg } from "../../Reducers/authReducer";
import GradeIcon from "@mui/icons-material/Grade";
import FlashOnIcon from "@mui/icons-material/FlashOn";
import IconButton from "@mui/joy/IconButton";
import { useGetUserDataQuery } from "../../services/login";
import formatPrice from "../formatPrice";
import ProductCountries from "../Header/ProductCountries";
import "./HomeProductCards.scss";

var moment = require("moment");

function HomeProductCards({ productData, heading, isLoading }) {
  const { t } = useTranslation();
  const { userId, userToken, countryCode } = useSelector((state) => state.auth);
  const dispatch = useDispatch();
  const isMobcreen = useMediaQuery({ query: "(min-width: 0px) and (max-width: 768px)" });

  const { data: userData } = useGetUserDataQuery(
    { userId, userToken, countryCode },
    { count: 1 },
    { refetchOnMountOrArgChange: true }
  );

  const listRef = React.useRef(null);

  function productTitle(title) {
    return title
      ?.replace(/[^A-Za-z0-9]/g, " ")
      ?.replace(/[^\w\s]/gi, " ")
      ?.replace(/ /g, "-");
  }

  function addProductImage(data) {
    dispatch(addProductImg({ productImg: data }));
  }

  const handleError = (e) => {
    console.error("Error loading image:", e.target.src);
    e.target.src = "/Image-not-found.jpg";
  };

  function getDeliverydays(delivery) {
    const deliveryDaya = Number(delivery) + Number(userData?.contant?.delivery_days || 0);
    if (Number(deliveryDaya) > 0) {
      return `${t("delivery_by")} ${moment().add(14, "days").format("Do MMM")}`;
    }
    return `${t("delivery_by")} ${moment().add(14, "days").format("Do MMM")}`;
  }

  function ratingFucn(rating) {
    if (rating === 0 || rating === "0" || !rating) return false;
    return true;
  }

  const storeIds = [1, 3, 18, 19, 20, 21, 22, 23, 25, 11, 12, 26];
  function functionIdLink(id, pid, store_id) {
    return storeIds?.includes(Number(store_id)) ? pid : id;
  }

  const handleScroll = (direction) => {
    if (listRef.current) {
      const card = listRef.current.querySelector(".lx-carousel__item");
      const itemWidth = card ? card.offsetWidth + 14 : 220;
      listRef.current.scrollBy({
        left: direction === "next" ? itemWidth * 3 : -itemWidth * 3,
        behavior: "smooth",
      });
    }
  };

  if (isLoading) {
    return (
      <>
        {productData?.length > 0 && <div className="cardTitle">{heading}</div>}
        <ProductCarouselSkeleton />
      </>
    );
  }

  if (!productData?.length) return null;

  return (
    <div className="lx-carousel-section">
      <div className="cardTitle">{heading}</div>

      <div className="lx-carousel-wrap">
        {productData?.length > 5 && (
          <>
            <IconButton
              className="lx-carousel__nav lx-carousel__nav--prev"
              variant="outlined"
              size="sm"
              onClick={() => handleScroll("prev")}
              aria-label={t("previous")}
            >
              <svg width="18" fill="currentColor" viewBox="0 0 16 16">
                <path
                  fillRule="evenodd"
                  d="M11.354 1.646a.5.5 0 0 1 0 .708L5.707 8l5.647 5.646a.5.5 0 0 1-.708.708l-6-6a.5.5 0 0 1 0-.708l6-6a.5.5 0 0 1 .708 0"
                />
              </svg>
            </IconButton>
            <IconButton
              className="lx-carousel__nav lx-carousel__nav--next"
              variant="outlined"
              size="sm"
              onClick={() => handleScroll("next")}
              aria-label={t("next")}
            >
              <svg width="18" fill="currentColor" viewBox="0 0 16 16">
                <path
                  fillRule="evenodd"
                  d="M4.646 1.646a.5.5 0 0 1 .708 0l6 6a.5.5 0 0 1 0 .708l-6 6a.5.5 0 0 1-.708-.708L10.293 8 4.646 2.354a.5.5 0 0 1 0-.708"
                />
              </svg>
            </IconButton>
          </>
        )}

        <div className="lx-carousel" ref={listRef}>
          {productData?.map((item, i) => (
            <div className="lx-carousel__item" key={i}>
              <Link
                className="lx-card"
                onClick={() => addProductImage(item?.image)}
                to={`/product/${item?.store_id}/${functionIdLink(
                  item?.id,
                  item?.pid,
                  item?.store_id
                )}/${productTitle(item?.title)}?country=${item?.country}`}
              >
                <div className="lx-card__media">
                  {item?.prices?.deal_value && (
                    <div className="lx-flash" aria-label={t("deal")}>
                      <FlashOnIcon style={{ fontSize: "1rem" }} />
                    </div>
                  )}

                  <img
                    className="lx-card__img"
                    src={item?.image}
                    alt={item?.title}
                    loading="lazy"
                    onError={handleError}
                  />

                  <div className="lx-delivery">
                    <span className={isMobcreen ? "lx-delivery__pill" : "lx-delivery__text"}>
                      {getDeliverydays(item.estimated_delivery)}
                    </span>
                  </div>
                </div>

                <div className="lx-card__body">
                  <h4 className="lx-title">{item?.title?.replace(/[^\w\s.,'/:]/gi, " ")}</h4>

                  <div className="lx-meta">
                    {item?.brand && <span className="lx-brand">{item?.brand}</span>}
                    {ratingFucn(item?.rating) && (
                      <span className="lx-rating">
                        <span className="lx-rating__value">{item?.rating}</span>
                        <GradeIcon className="lx-rating__icon" />
                      </span>
                    )}
                  </div>

                  <div className="lx-price-row">
                    {Number(item?.prices?.value) === 0 ? (
                      <span className="lx-chip lx-chip--muted">{t("Check Price")}</span>
                    ) : (
                      <div className="lx-price">
                        <span className="lx-price__current">
                          {formatPrice(item?.prices?.value, item?.prices?.currency, 0)}
                        </span>
                        {item?.prices?.deal_value && (
                          <span className="lx-price__was">
                            {formatPrice(item?.prices?.price, item?.prices?.currency, 0)}
                          </span>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="lx-footer">
                    <div className="lx-origin">
                      <span className="lx-origin__label">{t("From")}</span>
                      <img
                        className="lx-origin__flag"
                        loading="lazy"
                        src={`https://flagcdn.com/w20/${item?.country?.toLowerCase()}.png`}
                        srcSet={`https://flagcdn.com/w40/${item?.country?.toLowerCase()}.png 2x`}
                        alt=""
                      />
                      <span className="lx-origin__name">{ProductCountries(item?.country)}</span>
                    </div>
                  </div>
                </div>
              </Link>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default HomeProductCards;